import {useEffect, useId, useState} from "react";
import {Button} from "../basic/button.tsx";
import {Input} from "../basic/input.tsx";
import {Label} from "../basic/label.tsx";
import {MailCheck, RotateCw, TimerReset, XCircle} from "lucide-react";
import {useTranslationWithKeys} from "../../hooks/useTranslationWithKeys";

function formatRemaining(ms: number): string {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Presentational one-time-code panel for a pending verified email change.
 * The parent owns polling and mutations; {@link keyPrefix} selects the copy.
 */
export function EmailCodeVerification(
    {
        newEmail,
        expiresAt,
        statusError,
        cancelling,
        confirming,
        resending,
        resendAvailableAt,
        keyPrefix,
        onConfirm,
        onResend,
        onCancel
    }: {
        newEmail?: string | null;
        expiresAt?: string | null;
        statusError: boolean;
        cancelling: boolean;
        confirming: boolean;
        resending: boolean;
        resendAvailableAt: number;
        /** i18n key prefix providing the panel's strings. */
        keyPrefix: string;
        onConfirm: (code: string) => void;
        onResend: () => void;
        onCancel: () => void;
    }
) {
    const {t} = useTranslationWithKeys();
    const [now, setNow] = useState(() => Date.now());
    const [code, setCode] = useState("");
    const codeInputId = useId();

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    const deadline = expiresAt ? new Date(expiresAt).getTime() : null;
    const remaining = deadline !== null ? deadline - now : null;
    const expired = remaining !== null && remaining <= 0;

    const resendIn = Math.max(0, Math.ceil((resendAvailableAt - now) / 1000));
    const resendReady = resendIn === 0;

    return (
        <div className="flex flex-col items-center gap-4 py-8 text-center">
            {expired
                ? <TimerReset className="h-12 w-12 text-muted-foreground" aria-hidden/>
                : <MailCheck className="h-12 w-12 text-primary" aria-hidden/>}
            <h3 className="text-lg font-semibold">
                {expired
                    ? t(`${keyPrefix}.pendingExpiredTitle`)
                    : t(`${keyPrefix}.pendingTitle`)}
            </h3>
            <p className="max-w-md text-muted-foreground whitespace-pre-line">
                {expired
                    ? t(`${keyPrefix}.pendingExpiredDescription`)
                    : t(`${keyPrefix}.pendingDescription`, {email: newEmail ?? ""})}
            </p>
            {remaining !== null && !expired && (
                <p className="font-mono text-2xl tabular-nums" aria-live="polite">
                    {formatRemaining(remaining)}
                </p>
            )}
            {!expired && (
                <form
                    className="flex w-full max-w-xs flex-col gap-3"
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (code.trim()) {
                            onConfirm(code.trim());
                        }
                    }}
                >
                    <Label htmlFor={codeInputId} className="sr-only">
                        {t(`${keyPrefix}.codeLabel`)}
                    </Label>
                    <Input
                        id={codeInputId}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder={t(`${keyPrefix}.codePlaceholder`)}
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        disabled={confirming}
                    />
                    <Button type="submit" className="rounded-full px-8" disabled={confirming || !code.trim()}>
                        {confirming
                            ? t(`${keyPrefix}.confirming`)
                            : t(`${keyPrefix}.confirmCode`)}
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        className="rounded-full px-8 text-muted-foreground"
                        disabled={!resendReady || resending || confirming}
                        onClick={onResend}
                    >
                        <RotateCw className="mr-2 h-4 w-4" aria-hidden/>
                        {resending
                            ? t(`${keyPrefix}.resending`)
                            : resendReady
                                ? t(`${keyPrefix}.resend`)
                                : t(`${keyPrefix}.resendIn`, {seconds: resendIn})}
                    </Button>
                </form>
            )}
            {statusError && (
                <p className="text-sm text-destructive">{t(`${keyPrefix}.statusError`)}</p>
            )}
            <Button
                variant="outline"
                className="rounded-full px-8"
                disabled={cancelling}
                onClick={onCancel}
            >
                <XCircle className="mr-2 h-4 w-4" aria-hidden/>
                {cancelling
                    ? t(`${keyPrefix}.cancelling`)
                    : t(`${keyPrefix}.cancelPending`)}
            </Button>
        </div>
    );
}
